/* Apple-Style Scroll Animations for FiGGYZ */
/* Premium scroll effects inspired by Apple product pages */

(function() {
  'use strict';

  // Animation configuration
  const ANIMATION_CONFIG = {
    // Easing curves (Apple's signature bezier curves)
    easing: {
      smooth: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
      spring: 'cubic-bezier(0.175, 0.885, 0.32, 1.275)',
      apple: 'cubic-bezier(0.4, 0, 0.2, 1)',
      dramatic: 'cubic-bezier(0.19, 1, 0.22, 1)'
    },
    
    // Timing for different element types
    timing: {
      image: { duration: 1200, delay: 0 },
      text: { duration: 800, delay: 100 },
      title: { duration: 1000, delay: 200 },
      card: { duration: 900, delay: 0 }
    },
    
    // Animation thresholds
    thresholds: {
      enter: 0.15,    // When element starts animating (15% visible)
      complete: 0.5   // When element is considered fully visible
    }
  };

  // Performance monitoring
  let frameCount = 0;
  let lastFPSCheck = performance.now();
  let isHighPerformance = true;

  // Initialize scroll animations when DOM is ready
  document.addEventListener('DOMContentLoaded', function() {
    initializeScrollAnimations();
    initializePerformanceMonitoring();
    setupReducedMotionSupport();
  });

  function initializeScrollAnimations() {
    console.log('🎬 Initializing Apple-style scroll animations...');
    
    // Setup different animation types
    setupImageAnimations();
    setupTextAnimations();
    setupSectionTransitions();
    setupParallaxEffects();
    setupStaggeredAnimations();
    
    console.log('✨ Scroll animations initialized successfully');
  }

  // Image Animations - Scale & Fade In
  function setupImageAnimations() {
    const images = document.querySelectorAll('.section-image-top img, .image-container img, .feature-image-rounded img');
    
    images.forEach((img, index) => {
      // Add animation data attributes
      img.setAttribute('data-scroll-animation', 'image-reveal');
      img.setAttribute('data-animation-delay', index * 100);
      
      // Set initial state
      img.style.opacity = '0';
      img.style.transform = 'scale(0.95) translateY(30px)';
      img.style.transition = `all ${ANIMATION_CONFIG.timing.image.duration}ms ${ANIMATION_CONFIG.easing.apple}`;
      
      // Setup intersection observer
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const delay = parseInt(entry.target.getAttribute('data-animation-delay')) || 0;
            
            setTimeout(() => {
              entry.target.style.opacity = '1';
              entry.target.style.transform = 'scale(1) translateY(0px)';
              entry.target.classList.add('animated-in');
            }, delay);
            
            observer.unobserve(entry.target);
          }
        });
      }, {
        threshold: ANIMATION_CONFIG.thresholds.enter,
        rootMargin: '50px 0px'
      });
      
      observer.observe(img);
    });
  }

  // Text Animations - Slide Up & Fade
  function setupTextAnimations() {
    // Animate section titles with character-by-character reveal
    const titles = document.querySelectorAll('.section-title');
    titles.forEach(setupTitleAnimation);
    
    // Animate all text content in .section-text-content containers
    const sectionTextContainers = document.querySelectorAll('.section-text-content');
    
    sectionTextContainers.forEach(container => {
      // Get all direct children that should animate
      const children = container.querySelectorAll('p, ul, h3, h4');
      
      children.forEach((element, index) => {
        element.setAttribute('data-scroll-animation', 'text-reveal');
        element.style.opacity = '0';
        element.style.transform = 'translateY(25px)';
        element.style.transition = `all ${ANIMATION_CONFIG.timing.text.duration}ms ${ANIMATION_CONFIG.easing.apple} ${index * 80}ms`;
        
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              setTimeout(() => {
                entry.target.style.opacity = '1';
                entry.target.style.transform = 'translateY(0px)';
                
                // If this is a ul, animate its li children after a brief delay
                if (entry.target.tagName === 'UL') {
                  const listItems = entry.target.querySelectorAll('li');
                  listItems.forEach((li, liIndex) => {
                    li.style.opacity = '0';
                    li.style.transform = 'translateY(15px)';
                    li.style.transition = `all 400ms ${ANIMATION_CONFIG.easing.apple}`;
                    
                    setTimeout(() => {
                      li.style.opacity = '1';
                      li.style.transform = 'translateY(0px)';
                    }, liIndex * 100 + 200);
                  });
                }
              }, index * 80);
              
              observer.unobserve(entry.target);
            }
          });
        }, {
          threshold: ANIMATION_CONFIG.thresholds.enter,
          rootMargin: '30px 0px'
        });
        
        observer.observe(element);
      });
    });
    
    // Also animate other text elements outside section-text-content
    const otherTextElements = document.querySelectorAll('.special-feature p, .feature-column li');
    otherTextElements.forEach((element, index) => {
      element.setAttribute('data-scroll-animation', 'text-reveal');
      element.style.opacity = '0';
      element.style.transform = 'translateY(25px)';
      element.style.transition = `all ${ANIMATION_CONFIG.timing.text.duration}ms ${ANIMATION_CONFIG.easing.apple} ${index * 50}ms`;
      
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              entry.target.style.opacity = '1';
              entry.target.style.transform = 'translateY(0px)';
            }, index * 50);
            
            observer.unobserve(entry.target);
          }
        });
      }, {
        threshold: ANIMATION_CONFIG.thresholds.enter,
        rootMargin: '30px 0px'
      });
      
      observer.observe(element);
    });
  }

  // Advanced Title Animation - Letter by Letter (Preserving Original HTML)
  function setupTitleAnimation(title) {
    // Store the original HTML to preserve existing styling
    const originalHTML = title.innerHTML;
    
    // Simple fade-in animation that preserves existing structure
    title.style.opacity = '0';
    title.style.transform = 'translateY(30px)';
    title.style.transition = `all ${ANIMATION_CONFIG.timing.title.duration}ms ${ANIMATION_CONFIG.easing.apple}`;
    
    // Setup intersection observer for title animation
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setTimeout(() => {
            entry.target.style.opacity = '1';
            entry.target.style.transform = 'translateY(0px)';
          }, ANIMATION_CONFIG.timing.title.delay);
          
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.3,
      rootMargin: '20px 0px'
    });
    
    observer.observe(title);
  }

  // Section Background Parallax
  function setupParallaxEffects() {
    const sections = document.querySelectorAll('.figgyz-showcase-section');
    let ticking = false;
    
    function updateParallax() {
      const scrollY = window.pageYOffset;
      
      sections.forEach((section, index) => {
        if (!section.getBoundingClientRect) return;
        
        const rect = section.getBoundingClientRect();
        const sectionTop = rect.top + scrollY;
        const sectionHeight = rect.height;
        const windowHeight = window.innerHeight;
        
        // Only apply parallax to sections in viewport
        if (rect.bottom >= 0 && rect.top <= windowHeight) {
          // Calculate parallax offset
          const progress = (scrollY - sectionTop + windowHeight) / (sectionHeight + windowHeight);
          const parallaxOffset = (progress - 0.5) * 50; // Subtle parallax movement
          
          // Apply different effects to light vs dark sections
          if (section.classList.contains('section-light')) {
            section.style.transform = `translateY(${parallaxOffset * 0.3}px)`;
          } else if (section.classList.contains('section-dark')) {
            section.style.transform = `translateY(${parallaxOffset * -0.2}px)`;
          }
        }
      });
      
      ticking = false;
    }
    
    function requestParallaxUpdate() {
      if (!ticking && isHighPerformance) {
        requestAnimationFrame(updateParallax);
        ticking = true;
      }
    }
    
    // Throttled scroll listener
    window.addEventListener('scroll', requestParallaxUpdate, { passive: true });
    window.addEventListener('resize', requestParallaxUpdate);
  }

  // Section Transitions with Color Morphing
  function setupSectionTransitions() {
    const sections = document.querySelectorAll('.figgyz-showcase-section');
    const isMobile = window.innerWidth <= 768;
    
    sections.forEach((section, index) => {
      section.style.opacity = '0';
      section.style.transform = isMobile ? 'translateY(20px)' : 'translateY(50px)';  // Less transform on mobile
      section.style.transition = `all 1000ms ${ANIMATION_CONFIG.easing.apple}`;
      
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              entry.target.style.opacity = '1';
              entry.target.style.transform = 'translateY(0px)';
              
              // Add section-specific effects
              if (entry.target.classList.contains('section-dark')) {
                entry.target.style.boxShadow = 'inset 0 0 100px rgba(0,0,0,0.1)';
              }
            }, index * 100);
            
            observer.unobserve(entry.target);
          }
        });
      }, {
        threshold: window.innerWidth <= 768 ? 0.01 : 0.1,  // Lower threshold for mobile to ensure trigger
        rootMargin: window.innerWidth <= 768 ? '100px 0px 100px 0px' : '100px 0px'  // More forgiving margins on mobile
      });
      
      observer.observe(section);
      
      // Mobile failsafe: ensure sections become visible after a delay
      if (isMobile) {
        setTimeout(() => {
          if (section.style.opacity === '0') {
            section.style.opacity = '1';
            section.style.transform = 'translateY(0px)';
          }
        }, 2000 + (index * 200));  // Stagger the failsafe
      }
    });
  }

  // Staggered Animations for Grouped Elements
  function setupStaggeredAnimations() {
    // Animate feature columns with stagger
    const featureColumns = document.querySelectorAll('.feature-columns .feature-column');
    animateWithStagger(featureColumns, 200);
    
    // Animate special features grid
    const specialFeatures = document.querySelectorAll('.special-feature');
    animateWithStagger(specialFeatures, 300);
    
    // Animate dual image layout
    const dualImages = document.querySelectorAll('.dual-image-layout .image-container');
    animateWithStagger(dualImages, 150);
    
    // Animate franchise grid cards - left to right filling (much faster)
    const franchiseCards = document.querySelectorAll('.franchise-card');
    animateWithStagger(franchiseCards, 25, 'scale(0.95) translateY(10px)', 'scale(1) translateY(0px)');
    
    // Animate FiggyzDex items - left to right filling (very fast)
    const dexItems = document.querySelectorAll('.dex-item');
    animateWithStagger(dexItems, 15, 'scale(0.98) translateY(8px)', 'scale(1) translateY(0px)');
  }

  function animateWithStagger(elements, staggerDelay, initialTransform = 'translateY(40px) scale(0.95)', finalTransform = 'translateY(0px) scale(1)') {
    if (!elements.length) return;
    
    elements.forEach((element, index) => {
      element.style.opacity = '0';
      element.style.transform = initialTransform;
      // Faster animation duration for grid items
      const duration = staggerDelay < 30 ? 400 : ANIMATION_CONFIG.timing.card.duration;
      element.style.transition = `all ${duration}ms ${ANIMATION_CONFIG.easing.apple}`;
      element.style.willChange = 'transform, opacity';
      
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              entry.target.style.opacity = '1';
              entry.target.style.transform = finalTransform;
              entry.target.classList.add('stagger-animated');
            }, index * staggerDelay);
            
            observer.unobserve(entry.target);
          }
        });
      }, {
        threshold: 0.1, // Lower threshold for faster triggering
        rootMargin: '100px 0px' // Larger margin for earlier triggering
      });
      
      observer.observe(element);
    });
  }

  // Performance Monitoring
  function initializePerformanceMonitoring() {
    function checkPerformance() {
      frameCount++;
      const now = performance.now();
      
      if (now - lastFPSCheck >= 1000) {
        const fps = frameCount;
        
        // Adjust animations based on performance
        if (fps < 30) {
          isHighPerformance = false;
          document.body.classList.add('reduce-animations');
          console.warn('🐌 Low FPS detected, reducing animations');
        } else if (fps >= 45 && !isHighPerformance) {
          isHighPerformance = true;
          document.body.classList.remove('reduce-animations');
          console.log('🚀 Performance recovered, restoring animations');
        }
        
        frameCount = 0;
        lastFPSCheck = now;
      }
      
      requestAnimationFrame(checkPerformance);
    }
    
    checkPerformance();
  }

  // Reduced Motion Support
  function setupReducedMotionSupport() {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    
    function handleReducedMotion(e) {
      if (e.matches) {
        document.body.classList.add('prefers-reduced-motion');
        console.log('🎯 Reduced motion preference detected');
      } else {
        document.body.classList.remove('prefers-reduced-motion');
      }
    }
    
    prefersReducedMotion.addListener(handleReducedMotion);
    handleReducedMotion(prefersReducedMotion);
  }

  // Enhanced Image Loading with Progressive Enhancement
  function setupProgressiveImageLoading() {
    const images = document.querySelectorAll('img[loading="lazy"]');
    
    images.forEach(img => {
      if ('loading' in HTMLImageElement.prototype) {
        // Browser supports native lazy loading
        img.style.transition = `opacity 400ms ${ANIMATION_CONFIG.easing.apple}`;
        
        img.addEventListener('load', function() {
          this.style.opacity = '1';
        });
      } else {
        // Fallback for browsers without native lazy loading
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const img = entry.target;
              img.src = img.getAttribute('data-src') || img.src;
              img.style.opacity = '1';
              observer.unobserve(img);
            }
          });
        });
        
        observer.observe(img);
      }
    });
  }

  // Export for debugging and dynamic content
  window.FiggyzScrollAnimations = {
    config: ANIMATION_CONFIG,
    performance: () => ({ isHighPerformance, frameCount }),
    reinitialize: initializeScrollAnimations,
    animateStagger: animateWithStagger
  };

})();